import { LABELS, todayInTimezone } from '@mm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Unlink } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Badge, Card, EmptyState } from '../../components/ui/display';
import { Field, Input, Textarea } from '../../components/ui/inputs';
import { Modal } from '../../components/ui/Modal';
import { type Option, RemoteMultiSelect } from '../../components/ui/SearchableSelect';
import { Tabs } from '../../components/ui/Tabs';
import { useToast } from '../../components/ui/Toast';
import { useAccountDisplayMode } from '../../hooks/useAccountDisplayMode';
import { DataTable } from '../../tables/DataTable';
import type {
  AppAccounts,
  AppAdvertisingAssignment,
  AppRevenueAssignment,
  AssignmentPeriod,
} from '../../types/models';
import { accountLabel } from '../../utils/accounts';
import { formatDate } from '../../utils/format';
import { useAuth } from '../auth/AuthProvider';
import { durationLabel } from './duration';

export type AssignmentKind = 'advertising' | 'revenue';
type AnyAssignment = AppAdvertisingAssignment | AppRevenueAssignment;

const KIND = {
  advertising: {
    title: 'Ads accounts',
    path: 'advertising-accounts',
    pickerResource: 'advertising-accounts',
    detail: (id: string) => `/ads-accounts/${id}`,
    singular: 'ads account',
  },
  revenue: {
    title: 'Revenue accounts',
    path: 'revenue-accounts',
    pickerResource: 'revenue-accounts',
    detail: (id: string) => `/revenue-accounts/${id}`,
    singular: 'revenue account',
  },
} as const;

export const appAccountsKey = (appId: string, kind: AssignmentKind) =>
  ['apps', appId, 'assignments', kind] as const;

const accountOf = (r: AnyAssignment) =>
  'advertisingAccount' in r ? r.advertisingAccount : r.revenueAccount;
const typeOf = (r: AnyAssignment) =>
  'advertisingAccount' in r
    ? r.advertisingAccount.platform?.name
    : r.revenueAccount.revenueSource.name;

/**
 * App page: accounts the app uses now, accounts it used before, and the full
 * assignment history. Admins can assign several accounts at once or end an
 * assignment (history is never deleted).
 */
export function AppAccountsCard({
  appId,
  appName,
  appActive = true,
  kind,
}: {
  appId: string;
  appName: string;
  /** Inactive apps cannot get new accounts (history stays visible). */
  appActive?: boolean;
  kind: AssignmentKind;
}) {
  const k = KIND[kind];
  const { can } = useAuth();
  const canWrite = can('masters.write');
  const canAdd = canWrite && appActive;
  const mode = useAccountDisplayMode();
  const [tab, setTab] = useState<'current' | 'previous' | 'history'>('current');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<AnyAssignment | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: appAccountsKey(appId, kind),
    queryFn: () => api.get<AppAccounts<AnyAssignment>>(`/apps/${appId}/${k.path}`),
  });

  const label = (r: AnyAssignment) => {
    const a = accountOf(r);
    return kind === 'advertising' ? accountLabel(a, mode) : a.accountName;
  };

  const rows = data?.[tab] ?? [];
  return (
    <Card
      title={k.title}
      bodyClassName="p-0"
      description={canWrite && !appActive ? 'Activate the app to assign more accounts.' : undefined}
      actions={
        canAdd ? (
          <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>
            Add accounts
          </Button>
        ) : undefined
      }
    >
      <div className="px-5 pt-2">
        <Tabs
          tabs={[
            { id: 'current', label: `Current (${data?.current.length ?? 0})` },
            { id: 'previous', label: `Previously used (${data?.previous.length ?? 0})` },
            { id: 'history', label: 'History' },
          ]}
          active={tab}
          onChange={(id) => setTab(id as typeof tab)}
        />
      </div>
      <DataTable<AnyAssignment>
        tableId={`app-accounts-${kind}`}
        rows={rows}
        loading={isLoading}
        rowKey={(r) => r.id}
        columns={[
          {
            key: 'account',
            header: 'Account',
            render: (r) => (
              <div className="min-w-0">
                <Link
                  to={k.detail(accountOf(r).id)}
                  className="font-medium text-slate-900 hover:text-brand-700"
                >
                  {label(r)}
                </Link>
                <p className="text-xs text-slate-500">
                  {[
                    typeOf(r),
                    'loginEmail' in accountOf(r)
                      ? (accountOf(r) as { loginEmail?: string | null }).loginEmail
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
            ),
          },
          { key: 'assignedOn', header: 'Added', render: (r) => formatDate(r.assignedOn) },
          {
            key: 'removedOn',
            header: 'Removed',
            render: (r) => (r.removedOn ? formatDate(r.removedOn) : '—'),
          },
          {
            key: 'duration',
            header: 'Duration',
            align: 'right',
            render: (r) => durationLabel(r.durationDays),
          },
          {
            key: 'status',
            header: 'Status',
            render: (r) => (
              <Badge tone={r.status === 'ACTIVE' ? 'green' : 'slate'}>
                {LABELS.assignmentStatus[r.status]}
              </Badge>
            ),
          },
          ...(canWrite
            ? [
                {
                  key: 'actions',
                  header: '',
                  align: 'right' as const,
                  render: (r: AnyAssignment) =>
                    r.status === 'ACTIVE' ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Unlink className="size-4" />}
                        onClick={() => setRemoving(r)}
                      >
                        Remove
                      </Button>
                    ) : null,
                },
              ]
            : []),
        ]}
        empty={
          <EmptyState
            title={
              tab === 'current'
                ? `No ${k.singular}s assigned`
                : tab === 'previous'
                  ? 'No previously used accounts'
                  : 'No history yet'
            }
            description={
              tab === 'current' && canWrite
                ? `Use “Add accounts” to assign existing ${k.singular}s.`
                : undefined
            }
          />
        }
      />
      {adding && (
        <AddAccountsModal
          appId={appId}
          appName={appName}
          kind={kind}
          onClose={() => setAdding(false)}
        />
      )}
      {removing && (
        <RemoveAssignmentModal
          appId={appId}
          appName={appName}
          kind={kind}
          assignment={removing}
          accountName={label(removing)}
          onClose={() => setRemoving(null)}
        />
      )}
    </Card>
  );
}

function useInvalidate(appId: string, kind: AssignmentKind) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: appAccountsKey(appId, kind) });
    // Account lists/details show their current apps; the morning entry rows follow assignments.
    void queryClient.invalidateQueries({ queryKey: ['resource'] });
    void queryClient.invalidateQueries({ queryKey: ['morning-entry'] });
    void queryClient.invalidateQueries({ queryKey: ['account-apps'] });
  };
}

function errorText(err: unknown) {
  if (!(err instanceof ApiError)) return 'Request failed';
  const details = err.errors.map((e) => e.message).filter((m) => m !== err.message);
  return [err.message, ...details].join(' · ');
}

function AddAccountsModal({
  appId,
  appName,
  kind,
  onClose,
}: {
  appId: string;
  appName: string;
  kind: AssignmentKind;
  onClose: () => void;
}) {
  const k = KIND[kind];
  const toast = useToast();
  const mode = useAccountDisplayMode();
  const invalidate = useInvalidate(appId, kind);
  const [selected, setSelected] = useState<Option[]>([]);
  const [assignedOn, setAssignedOn] = useState(todayInTimezone());
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.post(`/apps/${appId}/${k.path}`, {
        accountIds: selected.map((o) => o.value),
        assignedOn,
        notes,
      }),
    onSuccess: () => {
      toast.success(
        `${selected.length} account${selected.length === 1 ? '' : 's'} assigned to ${appName}`,
      );
      invalidate();
      onClose();
    },
    onError: (err) => setError(errorText(err)),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Add ${k.singular}s to ${appName}`}
      description="Only existing, active accounts not already assigned to this app are listed."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            disabled={!selected.length}
            onClick={() => save.mutate()}
          >
            Assign {selected.length || ''}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Accounts" htmlFor="assign-accounts" required>
          <RemoteMultiSelect
            inputId="assign-accounts"
            value={selected}
            onChange={setSelected}
            placeholder="Search by name, ID or email…"
            remote={{
              resource: k.pickerResource,
              params: { status: 'ACTIVE', notAssignedToAppId: appId },
              label: (r) => (kind === 'advertising' ? accountLabel(r, mode) : `${r.accountName}`),
              hint: (r) =>
                kind === 'advertising'
                  ? [r.platform?.name, r.loginEmail, r.managerAccount?.name]
                      .filter(Boolean)
                      .join(' · ')
                  : [r.revenueSource?.name, r.accountIdentifier, r.currencyCode]
                      .filter(Boolean)
                      .join(' · '),
            }}
          />
        </Field>
        <Field
          label="Used from"
          htmlFor="assign-date"
          help="First day the account counts for this app (can be in the past)"
        >
          <Input
            id="assign-date"
            type="date"
            value={assignedOn}
            max={todayInTimezone()}
            onChange={(e) => setAssignedOn(e.target.value)}
          />
        </Field>
        <Field label="Notes" htmlFor="assign-notes">
          <Textarea
            id="assign-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
        {error && (
          <p className="text-sm text-rose-600" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

function RemoveAssignmentModal({
  appId,
  appName,
  kind,
  assignment,
  accountName,
  onClose,
}: {
  appId: string;
  appName: string;
  kind: AssignmentKind;
  assignment: AssignmentPeriod & ({ advertisingAccountId: string } | { revenueAccountId: string });
  accountName: string;
  onClose: () => void;
}) {
  const k = KIND[kind];
  const toast = useToast();
  const invalidate = useInvalidate(appId, kind);
  const [removedOn, setRemovedOn] = useState(todayInTimezone());
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const accountId =
    'advertisingAccountId' in assignment
      ? assignment.advertisingAccountId
      : assignment.revenueAccountId;

  const save = useMutation({
    mutationFn: () => api.delete(`/apps/${appId}/${k.path}/${accountId}`, { removedOn, notes }),
    onSuccess: () => {
      toast.success(`${accountName} removed from ${appName}`);
      invalidate();
      onClose();
    },
    onError: (err) => setError(errorText(err)),
  });

  return (
    <Modal
      open
      size="sm"
      onClose={onClose}
      title={`Remove ${accountName}?`}
      description={`The account stops counting for ${appName} after the last active day. Its history and entries are kept.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" loading={save.isPending} onClick={() => save.mutate()}>
            Remove
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="Last active day"
          htmlFor="remove-date"
          help={`Assigned since ${formatDate(assignment.assignedOn)}`}
        >
          <Input
            id="remove-date"
            type="date"
            value={removedOn}
            min={assignment.assignedOn}
            max={todayInTimezone()}
            onChange={(e) => setRemovedOn(e.target.value)}
          />
        </Field>
        <Field label="Notes" htmlFor="remove-notes">
          <Textarea
            id="remove-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
        {error && (
          <p className="text-sm text-rose-600" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
